import { memo, useEffect, useRef, useState } from "react";
import { TemplateEditorProps } from "./types";
import { Badge } from "../Badge";
import { ReactSVG } from "react-svg";
import { Modal } from "../Modal";
import ModalHeader from "../Modal/ModalHeader";
import ModalBody from "../Modal/ModalBody";
import { ZPosition } from "../Modal/types";

const LINE_HEIGHT_PX = 18;

const TemplateEditorComponent: React.FC<TemplateEditorProps> = ({
  name,
  value,
  onChange,
  tags,
  label,
  placeholder,
  error,
  required,
  rows = 10,
  cssClassName,
}) => {
  const editorRef = useRef<HTMLDivElement | null>(null);

  const minHeight = rows * LINE_HEIGHT_PX + 26;
  const [height, setHeight] = useState<number>(minHeight);
  const [showTags, setShowTags] = useState<boolean>(false);

  /**
   * Sync external value → editor
   */
  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value || "";
    }
  }, [value]);

  /**
   * Notify parent on change
   */
  const handleInput = () => {
    onChange({ target: { name, value: editorRef.current?.innerHTML || "", type: "text" } });
  };

  /**
   * ENTER = novi red (<br>)
   */
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      document.execCommand("insertLineBreak");
      handleInput();
    }
  };

  /**
   * EXEC COMMAND (bold / underline)
   */
  const exec = (command: "bold" | "underline") => {
    editorRef.current?.focus();
    document.execCommand(command);
    handleInput();
  };

  /**
   * INSERT TEMPLATE TAG AT CURSOR
   */
  const insertTag = (tagKey: string) => {
    editorRef.current?.focus();

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    range.deleteContents();

    const textNode = document.createTextNode(`#${tagKey}#`);
    range.insertNode(textNode);

    range.setStartAfter(textNode);
    range.collapse(true);

    selection.removeAllRanges();
    selection.addRange(range);

    handleInput();
    setShowTags(false);
  };

  /**
   * RESIZE HANDLER
   */
  const startResize = (e: React.MouseEvent) => {
    e.preventDefault();

    const startY = e.clientY;
    const startHeight = height;

    const onMouseMove = (event: MouseEvent) => {
      const diff = event.clientY - startY;
      setHeight(Math.max(minHeight, startHeight + diff));
    };

    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  return (
    <div className="w-full grid">
      {/* LABEL */}
      {label && (
        <label className="block mb-2 text-sm font-medium text-gray-700">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}

      {/* TOOLBAR */}
      <div className="flex justify-between gap-2 ring-1 ring-slate-300 rounded-t-sm px-2 py-2 bg-white">
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => exec("bold")}
            className="px-2 py-1 border rounded font-bold w-10 bg-slate-50 hover:bg-slate-100"
          >
            B
          </button>

          <button
            type="button"
            onClick={() => exec("underline")}
            className="px-2 py-1 border rounded underline w-10 bg-slate-50 hover:bg-slate-100"
          >
            U
          </button>
        </div>

        <button
          type="button"
          onClick={() => setShowTags(true)}
          className="px-2 py-1.5 text-sm border rounded bg-slate-50 hover:bg-slate-100"
        >
          + Dodaj ključnu riječ
        </button>
      </div>

      {/* EDITOR WRAPPER */}
      <div className="relative" style={{ height }}>
        {/* EDITOR */}
        <div
          ref={editorRef}
          contentEditable
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          suppressContentEditableWarning
          style={{
            height: "100%",
            lineHeight: `${LINE_HEIGHT_PX}px`,
          }}
          className={`
            whitespace-pre-wrap break-words overflow-y-auto
            cursor-default rounded-b-sm py-3 pl-3 pr-3 text-left
            ring-1 sm:text-sm focus:outline-none bg-slate-50 text-gray-800
            ${error ? "ring-red-400 bg-red-50" : "ring-slate-300"}
            ${cssClassName ?? ""}
          `}
        />

        {/* RESIZE HANDLE */}
        <div
          onMouseDown={startResize}
          className="absolute bottom-1 right-1 cursor-ns-resize p-1 rounded-full"
        >
          <ReactSVG
            src="/media/icons/resize.svg"
            className="w-4 h-4 stroke-[#667085]"
          />
        </div>
      </div>

      {/* PLACEHOLDER */}
      {!value && placeholder && (
        <div
          className="pointer-events-none text-gray-400 text-sm z-10"
          style={{
            marginTop: `-${height}px`,
            padding: "12px",
          }}
        >
          {placeholder}
        </div>
      )}

      {/* ERROR */}
      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}

      {/* TAG MODAL */}
      <Modal
        show={showTags}
        onHide={() => setShowTags(false)}
        position="centered"
        zPosition={ZPosition.Top}
      >
        <ModalHeader
          title="Dodavanje ključne riječi"
          icon="/media/images/add-icon.svg"
          onHide={() => setShowTags(false)}
        />
        <ModalBody>
          <div className="px-4 overflow-y-scroll max-h-[500px]">
            {tags.length > 0 && (
              <div className="mt-3 flex flex-col gap-2">
                {tags.map((tag) => (
                  <button
                    key={tag.key}
                    type="button"
                    className="flex flex-col items-start px-3 pt-2 pb-2.5 text-xs border rounded bg-white hover:bg-slate-50"
                    onClick={() => insertTag(tag.key)}
                  >
                    <div className="flex w-full justify-between gap-3 items-center">
                      <div className="text-sm">{tag.label}</div>
                      <Badge
                        text={`#${tag.key}#`}
                        variant="info"
                      />
                    </div>
                    <div>{tag.description}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </ModalBody>
      </Modal>
    </div>
  );
};

export const TemplateEditor = memo(TemplateEditorComponent);
